<template>
  <div class="row" v-if="canSearch">
    <div class="col-md-12 card">
      <div class="card-body row">
        <div class="col-sm-12">
          <div class="pull-right">
            <label class="inline-block form-group">
              <el-input type="search" clearable class="input-sm" placeholder="Search Item Codes" v-model="ui.searchQuery" aria-controls="datatables" style="width:300px" />
            </label>
            <el-select class="select select-filter"
                       name="qbClassFilter"
                       clearable
                       size="large"
                       placeholder="Class"
                       v-model="ui.qbClassFilter">
              <el-option v-for="option in uniqueItemCodes"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <button type="button" v-if="canEdit" @click="newItemCode" class="btn btn-fill btn-info btn-new-task-name">
              New Item Code
            </button>
          </div>
        </div>
        <div class="col-sm-12">
          <el-table class="table-striped wrap-whole-words indexed-table-column-width"
                    :data="queriedData"
                    border
                    :max-height="tableHeight(400)"
                    style="width: 100%">
            <el-table-column
                :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
                align="center"
                label="#"
                type="index" />
            <el-table-column
              :min-width="30"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'name')"
              label="Name">
              <template slot-scope="props">
                <a href="#" @click.prevent="editItemCode(props.row.id)">{{ props.row.name }}</a>
              </template>
            </el-table-column>
            <el-table-column
              :min-width="30"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'qbClass')"
              label="QB Class">
              <template slot-scope="props">
                {{ props.row.qbClass }}
              </template>
            </el-table-column>
            <el-table-column
              label="Description"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'description')">
              <template slot-scope="props">
                {{ props.row.description }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="canEdit"
              width="105"
              fixed="right"
              label="Actions">
              <template slot-scope="props">
                <p-button type="success" size="sm" icon>
                  <i class="fa fa-edit" @click="editItemCode(props.row.id)"></i>
                </p-button>
                <p-button type="danger" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import {sync} from 'vuex-pathify'
  import {store} from 'src/store/store'
  import {mapGetters} from 'vuex'
  import {
      notifySuccess,
      notifyError,
      stringSort,
      uniqueKvpsForSelectFromArray,
      getTableHeight
  } from 'src/utilities/utils'
  import PButton from 'src/components/UIComponents/Button.vue'
  import Permissions from 'src/utilities/permissions'
  import {NO_CONTENT, OK} from 'http-status-codes'
  import Constants from 'src/utilities/constants'

  export default{
    components: {
      PButton
    },
    computed: {
      ...mapGetters({
        departments: 'departments/kvpsForSelect',
        tableData: 'quickbooks/results'
      }),
      ui: sync('quickbooks@ui'),
      uniqueItemCodes () {
        return uniqueKvpsForSelectFromArray(this.$store.get('quickbooks.results'), 'qbClass')
      },
      queriedData () {
        if (!this.ui.searchQuery && !this.ui.qbClassFilter) {
          return this.tableData
        }
        return this.tableData
          .filter((row) => {
            let isIncluded = false
            if (this.ui.searchQuery) {
              for (let key of this.propsToSearch) {
                if (row[key]) {
                  let rowValue = row[key].toString()
                  if (rowValue.includes && rowValue.toLowerCase().includes(this.ui.searchQuery.toLowerCase())) {
                    isIncluded = true
                  }
                }
              }
            }

            if (this.ui.qbClassFilter
              && row.qbClass
              && row.qbClass.toLowerCase() === this.ui.qbClassFilter.toLowerCase()) {
              isIncluded = true
            }

            return isIncluded
          })
      },
      canSearch() {
        return this.$can('read', Permissions.QB.ITEM_CODES)
      },
      canEdit() {
        return this.$can('edit', Permissions.QB.ITEM_CODES)
      }
    },
    data () {
      return {
        toDelete: null,
        searchQuery: '',
        propsToSearch: ['name', 'description'],
        qbClassFilter: '',
        constants: Constants
      }
    },
    methods: {
      newItemCode () {
        this.$store.commit('quickbooks/resetEdit')
        this.$eventHub.$emit('show-modal-1', 'Item Code', null, '600px')
      },
      editItemCode (id) {
        this.$eventHub.$emit('show-modal-1', 'Item Code', {id: id}, '600px')
      },
      callStringSort(a, b, prop) {
        return stringSort(a, b, prop)
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      async confirmDelete(itemCode) {
        let showButton = false
        this.toDelete = itemCode.id

        const responseTaskNames = await this.$store.dispatch('tasks/names/fetchFindByItemCodeId', itemCode.id)

        if (OK !== responseTaskNames.status) {
          notifyError(this, "Failed to retrieve Task Names. Unable to verify if ItemCode <strong>" + itemCode.name + "</strong> is in use. Delete cancelled.")
          return
        }

        if (responseTaskNames.data.length > 0){
          let message = 'You can not delete <strong>' + itemCode.name + '</strong> as it is referenced by the following task names.'
          const taskNames = responseTaskNames.data
          message += '<p><ul>'
          taskNames.forEach((taskNames) => {
            message += '<li>' + taskNames.name + '</li>'
          })
          message += '</ul></p>'
          message += '<p>If you really want to delete this Item Code, you must first remove associations to it in each of the task names listed above, then come back here and delete the Item Code.</p>'
          showButton = false

          this.showConfirmDelete(itemCode, message, showButton)
          return
        }

        const message = 'Are you sure you want to delete <strong>' + itemCode.name + '</strong>? This will completely remove the Item Code.'

        // happy path - QB Item Code can be deleted
        showButton = true
        this.showConfirmDelete(itemCode, message, showButton)
      },
      showConfirmDelete(itemCode, message, showButton) {
        const payload = {
          message,
          entity: itemCode,
          delete: this.delete,
          showButton
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete () {
        const response = await this.$store.dispatch('delete', {
          id: this.toDelete,
          endpoint: 'item-codes'
        })
        if (NO_CONTENT === response.status) {
          this.$store.commit('quickbooks/delete', this.toDelete)
          notifySuccess(this, 'Item Code Deleted Successfully')
        } else {
          notifyError(this, response.message)
        }
      },
      async fetchAllItemCodes () {
        const includeActive = true
        await store.dispatch('quickbooks/fetchAll', includeActive)
      }
    },
    async beforeMount () {
      await this.fetchAllItemCodes()
      this.$eventHub.$on('itemCodeSaved', this.fetchAllItemCodes)
    },
    destroyed () {
      this.$eventHub.$off('itemCodeSaved', this.fetchAllItemCodes)
    }
  }
</script>
<style scoped>
  .pull-right .inline-block {
    display: inline-block;
    margin-right: 12px;
  }
  .row {
    margin-bottom: 15px;
  }
  .select-filter {
    margin-right: 12px;
    width: 185px;
  }
</style>
